import { firstValueFrom } from 'rxjs';
import omit from 'lodash/omit';
import {
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DinteroCheckoutInstance } from '@dintero/checkout-web-sdk';
import { COUNTRY_CODE_LOCALE_CODE, IUserCompany, ngHelper } from '@cakeiteasy/web-utils';

import { IPaymentInfo, IWebhookUrls } from '../..';
import { PaymentApiService } from '../../../api-services/services/payment-api.service';
import { ICart } from '../../../cart';
import { IUser } from '../../../users';
import { PAYMENT_PERSON_TYPES, PAYMENT_PLATFORM_TYPES } from '../../payment.constants';
import { DinteroCheckoutService, IDinteroPaymentSession } from '../../services/dintero-checkout.service';
import { PaymentStorageService } from '../../services/payment-storage.service';

@Component({
  standalone: false,
  selector: 'dintero-payment',
  templateUrl: './dintero-payment.component.html',
  styleUrls: ['./dintero-payment.component.scss'],
})
export class DinteroPaymentComponent implements OnChanges, OnDestroy {
  public personType = input<string>();
  public user = input<IUser>();
  public countryCode = input<string>();
  public cart = input<ICart>();
  public queueProcessing = input<Promise<any>>();
  public webhookUrls = input<IWebhookUrls>();
  public rejectUrl = input<string>();
  public giftCardCode = input<string>(null);
  public embed = input(false);
  public termsClickFn = input<any>();
  public cancelPaymentOnClose = input(false);
  public useCompanySearcher = input(false);
  public bakeryId = input<number>();
  // Hides the fields that depend on the chosen payment method (reference, confirm, terms) until one is chosen
  public hidePaymentMethodFields = input(false);

  @ViewChild('embedContainer', { static: false }) embedContainer: ElementRef<HTMLDivElement>;
  @ViewChild('modalContainer', { static: false }) modalContainer: ElementRef<HTMLDivElement>;

  public onSuccess = output<string>();
  public onFail = output<any>();
  public onTermsClick = output<void>();
  public onError = output<any>();
  public onOrderCreate = output<string>();
  public onInfoUpdated = output<IPaymentInfo>();
  public onCompanySelected = output<IUserCompany>();
  public sessionActivityChanged = output<boolean>();
  public onBusy = output<Promise<any>>();

  public PAYMENT_PERSON_TYPES = PAYMENT_PERSON_TYPES;
  public isValid: boolean;
  public info: IPaymentInfo;
  public isSessionActive = false;

  public checkout: DinteroCheckoutInstance;

  private _paymentApi = inject(PaymentApiService);
  private _storage = inject(PaymentStorageService);
  private _dinteroCheckout = inject(DinteroCheckoutService);

  public ngOnChanges(changes: SimpleChanges) {
    const { dintero } = PAYMENT_PLATFORM_TYPES;
    ngHelper.ifChanged(changes, 'user', (user: IUser, prevUser: IUser) => {
      if (user) {
        this._storage.updateContactInfo(dintero, null, user, this.useCompanySearcher());
      }

      this.info = this._storage.getContactInfo(dintero);
    });

    ngHelper.ifChanged(changes, 'queueProcessing', (queueProcessing) => {
      if (!this.embed() || !this.isSessionActive) {
        return;
      }

      const promise$ = queueProcessing.then(() => {
        if (this.checkout) {
          this._dinteroCheckout.destroy(this.checkout);
          return this.submit(null);
        }
      });

      this.onBusy.emit(promise$);
    });
  }

  public ngOnDestroy() {
    if (this.checkout) {
      this._dinteroCheckout.destroy(this.checkout);
    }
  }

  private _getPaymentPayload(info: IPaymentInfo, code?: string) {
    let payload = { ...info, code };

    if (this.personType() === PAYMENT_PERSON_TYPES.PRIVATE) {
      payload = omit(payload, ['userCompanyName', 'invoiceCompanyId', 'invoiceCompanyOrgNumber']);
    }

    return {
      ...payload,
      ...this.webhookUrls(),
    };
  }

  public cancelSession() {
    if (this.checkout) {
      this._dinteroCheckout.destroy(this.checkout);
    }
    this.checkout = null;
    this.isSessionActive = false;
    this.sessionActivityChanged.emit(false);
  }

  public confirm($event) {
    const promise = this.queueProcessing() || Promise.resolve(null);
    this.onBusy.emit(promise.then(() => this.submit($event)));
  }

  public submit($event) {
    const payload = this._getPaymentPayload(this.info, this.giftCardCode());

    return firstValueFrom(this._paymentApi.createDinteroPayment(payload))
      .then((data: IDinteroPaymentSession) => {
        this.onOrderCreate.emit(data.sid);
        this.isSessionActive = true;
        this.sessionActivityChanged.emit(true);

        const onPayment = (event) => {
          this.onSuccess.emit(event.href);
        };

        const onError = (error: any) => {
          this.onError.emit(error);
        };

        const onCancel = () => {
          if (this.cancelPaymentOnClose() && data.paymentId) {
            firstValueFrom(this._paymentApi.cancelPayment(data.paymentId));
          }
          this.checkout = null;
          this.isSessionActive = false;
          this.sessionActivityChanged.emit(false);
        };

        this.checkout = null;

        const containerToEmbed = this.embed() ? this.embedContainer.nativeElement : this.modalContainer.nativeElement;

        return this._dinteroCheckout
          .createInlineCheckout({
            snippedData: data,
            onPayment: onPayment.bind(this),
            onError: onError.bind(this),
            onCancel: onCancel.bind(this),
            containerToEmbed,
            language: COUNTRY_CODE_LOCALE_CODE[this.countryCode()],
          })
          .then((checkout) => {
            this.checkout = checkout;
            return checkout;
          });
      })
      .catch((err) => {
        this.onFail.emit(err);
      });
  }

  public newsletterUpdate(isSubscribed: boolean) {
    const { dintero } = PAYMENT_PLATFORM_TYPES;
    this.info.subscribedOnNews = isSubscribed;
    this._storage.updateContactInfo(dintero, this.info);
  }

  public infoUpdated(paymentInfo: IPaymentInfo) {
    const { dintero } = PAYMENT_PLATFORM_TYPES;
    this.info = { ...this.info, ...paymentInfo };

    if (this.personType() === PAYMENT_PERSON_TYPES.COMPANY && this.useCompanySearcher()) {
      this.isValid = this.isValid && !!this.info.userCompanyName;
    }

    this._storage.updateContactInfo(dintero, this.info);
  }
}
